import React, { useState, useEffect } from 'react';
import {
  DocumentArrowUpIcon,
  DocumentIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  TrashIcon,
  XMarkIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { api } from '../../services/authService';
import apiService from '../../services/api';

const DOCUMENT_TYPES = [
  'CNH',
  'ASO (Atestado de Saúde Ocupacional)',
  'Certidão Criminal',
  'Certidão de Antecedentes',
  'Exame Toxicológico',
  'Exame Médico',
  'Comprovante de Endereço',
  'Outro'
];

const DriverDocumentsTab = ({ driverId, onCNHProcessed }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [processingOCR, setProcessingOCR] = useState(null);

  const [uploadData, setUploadData] = useState({
    file: null,
    documentType: 'Outro',
    description: ''
  });

  useEffect(() => {
    if (driverId) {
      loadDocuments();
    }
  }, [driverId]);

  const loadDocuments = async () => {
    if (!driverId) {
      console.log('⚠️ DriverDocumentsTab: driverId não fornecido');
      return;
    }

    try {
      setLoading(true);
      const response = await api.get(`/api/driver-documents/driver/${driverId}`);

      if (response.data.success) {
        setDocuments(response.data.documents || []);
      }
    } catch (error) {
      console.error('Erro ao carregar documentos:', error);
      toast.error('Erro ao carregar documentos');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tamanho (10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Arquivo muito grande! Máximo: 10MB');
        return;
      }

      // Validar tipo de arquivo
      const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        toast.error('Apenas arquivos PDF, JPG ou PNG são permitidos');
        return;
      }

      setUploadData({ ...uploadData, file });
    }
  };

  const handleUpload = async () => {
    if (!uploadData.file) {
      toast.error('Selecione um arquivo');
      return;
    }

    if (!uploadData.documentType) {
      toast.error('Selecione o tipo de documento');
      return;
    }

    try {
      setUploading(true);

      const formData = new FormData();
      formData.append('file', uploadData.file);
      formData.append('driverId', driverId);
      formData.append('documentType', uploadData.documentType);
      formData.append('description', uploadData.description);

      const response = await api.post('/api/driver-documents/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        toast.success('Documento enviado com sucesso!');
        setShowUploadModal(false);
        setUploadData({
          file: null,
          documentType: 'Outro',
          description: ''
        });
        loadDocuments();
      }
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      toast.error(error.response?.data?.error || 'Erro ao enviar documento');
    } finally {
      setUploading(false);
    }
  };

  const handleView = async (documentId) => {
    try {
      const document = documents.find(doc => doc.id === documentId);
      if (document) {
        setSelectedDocument(document);
        setShowViewModal(true);
      }
    } catch (error) {
      console.error('Erro ao visualizar documento:', error);
      toast.error('Erro ao visualizar documento');
    }
  };

  const handleDownload = async (documentId, fileName) => {
    try {
      const response = await api.get(`/api/driver-documents/download/${documentId}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Download iniciado!');
    } catch (error) {
      console.error('Erro ao fazer download:', error);
      toast.error('Erro ao fazer download');
    }
  };

  const handleDelete = async (documentId) => {
    if (!confirm('Tem certeza que deseja deletar este documento?')) {
      return;
    }

    try {
      const response = await api.delete(`/api/driver-documents/${documentId}`);

      if (response.data.success) {
        toast.success('Documento deletado com sucesso!');
        loadDocuments();
      }
    } catch (error) {
      console.error('Erro ao deletar documento:', error);
      toast.error('Erro ao deletar documento');
    }
  };

  const handleReadCNH = async (document) => {
    if (!onCNHProcessed) {
      toast.error('Funcionalidade de leitura de CNH não disponível');
      return;
    }

    try {
      setProcessingOCR(document.id);
      toast.loading('Processando CNH... Isso pode levar até 2 minutos.', {
        id: 'ocr-processing',
        duration: 120000
      });

      // Download do documento para enviar ao OCR
      const downloadResponse = await api.get(`/api/driver-documents/download/${document.id}`, {
        responseType: 'blob'
      });

      // Converter blob para File
      const file = new File([downloadResponse.data], document.file_name, {
        type: document.mime_type
      });

      // Enviar para OCR
      const formDataOCR = new FormData();
      formDataOCR.append('document', file);

      const response = await apiService.post('/api/ocr/process-cnh', formDataOCR, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 120000
      });

      if (response.data.success) {
        const cnhData = response.data.data;

        // Chamar callback para atualizar dados no formulário
        onCNHProcessed(cnhData);

        toast.dismiss('ocr-processing');
        toast.success(`CNH processada! ${response.data.fieldsExtracted} campos extraídos.`);
      } else {
        toast.dismiss('ocr-processing');
        toast.error('Não foi possível extrair dados do documento');
      }
    } catch (error) {
      console.error('Erro ao processar CNH:', error);
      toast.dismiss('ocr-processing');
      toast.error(error.response?.data?.error || 'Erro ao processar CNH');
    } finally {
      setProcessingOCR(null);
    }
  };

  // Processar OCR diretamente de um arquivo selecionado (modo temporário, sem salvar)
  const handleReadCNHFromFile = async () => {
    if (!onCNHProcessed) {
      toast.error('Funcionalidade de leitura de CNH não disponível');
      return;
    }

    if (!uploadData.file) {
      toast.error('Selecione um arquivo CNH primeiro');
      return;
    }

    if (uploadData.documentType !== 'CNH') {
      toast.error('Selecione o tipo "CNH" para usar a leitura automática');
      return;
    }

    try {
      setProcessingOCR('temp-file');
      toast.loading('Processando CNH... Isso pode levar até 2 minutos.', {
        id: 'ocr-processing',
        duration: 120000
      });

      // Enviar arquivo diretamente para OCR
      const formDataOCR = new FormData();
      formDataOCR.append('document', uploadData.file);

      const response = await apiService.post('/api/ocr/process-cnh', formDataOCR, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 120000
      });

      if (response.data.success) {
        const cnhData = response.data.data;

        // Chamar callback para atualizar dados no formulário
        onCNHProcessed(cnhData);

        toast.dismiss('ocr-processing');
        toast.success(`CNH processada! ${response.data.fieldsExtracted} campos extraídos. Agora preencha os dados restantes e salve.`);

        // Fechar modal após sucesso
        setShowUploadModal(false);
        setUploadData({
          file: null,
          documentType: 'Outro',
          description: ''
        });
      } else {
        toast.dismiss('ocr-processing');
        toast.error('Não foi possível extrair dados do documento');
      }
    } catch (error) {
      console.error('Erro ao processar CNH:', error);
      toast.dismiss('ocr-processing');
      toast.error(error.response?.data?.error || 'Erro ao processar CNH');
    } finally {
      setProcessingOCR(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-4">
      {/* Aviso para novo cadastro */}
      {!driverId && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <p className="text-blue-800 dark:text-blue-200 text-sm">
            ✨ <strong>Dica:</strong> Você pode fazer upload de uma CNH e usar a leitura automática para preencher os dados do cadastro! Após preencher os dados, clique em "Cadastrar" para salvar.
          </p>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Documentos do Motorista
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {driverId
              ? 'Armazene CNH, ASO, certidões e outros documentos obrigatórios'
              : 'Faça upload de uma CNH para preencher automaticamente os dados do cadastro'
            }
          </p>
        </div>

        <button
          type="button"
          onClick={() => setShowUploadModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <DocumentArrowUpIcon className="h-5 w-5 mr-2" />
          Adicionar Documento
        </button>
      </div>

      {/* Lista de Documentos */}
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-500 dark:text-gray-400 mt-2">Carregando documentos...</p>
        </div>
      ) : documents.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
          <DocumentIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400">Nenhum documento cadastrado</p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
            Clique em "Adicionar Documento" para começar
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {documents.map((doc) => (
            <div
              key={doc.id}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3 flex-1">
                  <DocumentIcon className="h-10 w-10 text-blue-500 flex-shrink-0 mt-1" />

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {doc.file_name}
                      </h4>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                        {doc.document_type}
                      </span>
                    </div>

                    {doc.description && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {doc.description}
                      </p>
                    )}

                    <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                      <span>{formatFileSize(doc.file_size)}</span>
                      <span>•</span>
                      <span>{formatDate(doc.created_at)}</span>
                      {doc.uploaded_by_name && (
                        <>
                          <span>•</span>
                          <span>por {doc.uploaded_by_name}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2 ml-4">
                  {/* Botão de OCR CNH - só aparece para documentos CNH */}
                  {doc.document_type === 'CNH' && onCNHProcessed && (
                    <button
                      type="button"
                      onClick={() => handleReadCNH(doc)}
                      disabled={processingOCR === doc.id}
                      className="p-2 text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Ler CNH e Preencher Dados"
                    >
                      {processingOCR === doc.id ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600"></div>
                      ) : (
                        <SparklesIcon className="h-5 w-5" />
                      )}
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={() => handleView(doc.id)}
                    className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                    title="Visualizar"
                  >
                    <EyeIcon className="h-5 w-5" />
                  </button>

                  <button
                    type="button"
                    onClick={() => handleDownload(doc.id, doc.file_name)}
                    className="p-2 text-gray-400 hover:text-green-600 dark:hover:text-green-400"
                    title="Download"
                  >
                    <ArrowDownTrayIcon className="h-5 w-5" />
                  </button>

                  <button
                    type="button"
                    onClick={() => handleDelete(doc.id)}
                    className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                    title="Deletar"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de Upload */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Adicionar Documento
              </h3>
              <button
                type="button"
                onClick={() => setShowUploadModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Tipo de Documento */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tipo de Documento *
                </label>
                <select
                  value={uploadData.documentType}
                  onChange={(e) => setUploadData({ ...uploadData, documentType: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {DOCUMENT_TYPES.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Upload de Arquivo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Arquivo * (máx. 10MB)
                </label>
                <input
                  type="file"
                  onChange={handleFileChange}
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
                {uploadData.file && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Arquivo selecionado: {uploadData.file.name} ({formatFileSize(uploadData.file.size)})
                  </p>
                )}
              </div>

              {/* Descrição */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Descrição (opcional)
                </label>
                <textarea
                  value={uploadData.description}
                  onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Ex: CNH vencimento 2027, ASO válido até dezembro/2025..."
                />
              </div>

              {/* Botões */}
              <div className="flex justify-between items-center pt-4">
                {/* Botão "Ler CNH" - só aparece quando tipo é CNH e não há driverId */}
                {!driverId && uploadData.documentType === 'CNH' && uploadData.file && (
                  <button
                    type="button"
                    onClick={handleReadCNHFromFile}
                    disabled={processingOCR === 'temp-file' || uploading}
                    className="inline-flex items-center px-4 py-2 border border-purple-600 rounded-md shadow-sm text-sm font-medium text-purple-600 bg-white dark:bg-gray-700 hover:bg-purple-50 dark:hover:bg-purple-900/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {processingOCR === 'temp-file' ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600 mr-2"></div>
                        Lendo CNH...
                      </>
                    ) : (
                      <>
                        <SparklesIcon className="h-5 w-5 mr-2" />
                        Ler CNH
                      </>
                    )}
                  </button>
                )}

                <div className="flex space-x-3 ml-auto">
                  <button
                    type="button"
                    onClick={() => setShowUploadModal(false)}
                    disabled={uploading || processingOCR === 'temp-file'}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Cancelar
                  </button>

                  {/* Botão de enviar só aparece quando há driverId (motorista já salvo) */}
                  {driverId && (
                    <button
                      type="button"
                      onClick={handleUpload}
                      disabled={uploading || !uploadData.file}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {uploading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Enviando...
                        </>
                      ) : (
                        <>
                          <DocumentArrowUpIcon className="h-5 w-5 mr-2" />
                          Enviar Documento
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Visualização */}
      {showViewModal && selectedDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-5xl w-full h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white truncate">
                  {selectedDocument.file_name}
                </h3>
                <div className="flex items-center space-x-2 mt-1">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                    {selectedDocument.document_type}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {formatFileSize(selectedDocument.file_size)}
                  </span>
                </div>
              </div>

              <div className="flex items-center space-x-2 ml-4">
                <button
                  type="button"
                  onClick={() => handleDownload(selectedDocument.id, selectedDocument.file_name)}
                  className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                  title="Download"
                >
                  <ArrowDownTrayIcon className="h-6 w-6" />
                </button>

                <button
                  type="button"
                  onClick={() => setShowViewModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  title="Fechar"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Body - Visualização do Documento */}
            <div className="flex-1 overflow-hidden bg-gray-100 dark:bg-gray-900">
              {selectedDocument.mime_type?.startsWith('image/') ? (
                // Imagem
                <div className="h-full flex items-center justify-center p-4">
                  <img
                    src={`/api/driver-documents/view/${selectedDocument.id}`}
                    alt={selectedDocument.file_name}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              ) : selectedDocument.mime_type === 'application/pdf' ? (
                // PDF
                <iframe
                  src={`/api/driver-documents/view/${selectedDocument.id}`}
                  className="w-full h-full"
                  title={selectedDocument.file_name}
                />
              ) : (
                // Outros tipos - mostrar mensagem
                <div className="h-full flex flex-col items-center justify-center p-8 text-center">
                  <DocumentIcon className="h-16 w-16 text-gray-400 mb-4" />
                  <p className="text-gray-600 dark:text-gray-400 mb-2">
                    Pré-visualização não disponível para este tipo de arquivo
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-500 mb-4">
                    Tipo: {selectedDocument.mime_type}
                  </p>
                  <button
                    type="button"
                    onClick={() => handleDownload(selectedDocument.id, selectedDocument.file_name)}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                    Fazer Download
                  </button>
                </div>
              )}
            </div>

            {/* Footer - Descrição (se existir) */}
            {selectedDocument.description && (
              <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  <span className="font-medium">Descrição:</span> {selectedDocument.description}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverDocumentsTab;
